import { Pool } from "interfaces/pool";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Entry } from "interfaces/entry";
import { Bet } from "interfaces/bet";
import HomepageBetForm from "components/homepage-pools-list/homepage-bet-form";
import cardFlipStyles from "components/homepage-pools-list/card-flip.module.scss";
import useGeolocation from "hooks/useGeolocation";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getGroupState, requestToJoinGroup } from "state/slices/group-slice";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus";
import { CONTEST_ENTRY_ID_QUERY_PARAM, CONTEST_ID_QUERY_PARAM, POOL_STATUS, SKIP } from "utils/constants.ts";
import { CheckRequestStatus } from "@xpointtech/xpoint-js";
import FrontCard from "./front-card";
import { fetchBets } from "state/slices/bets-slice";
import { getIntegratedAppState, } from "state/slices/integrated-app-slice";
import { useAuth } from "hooks/auth";
import { setWalletIsStale } from "state/slices/wallet-slice";
import { isPoolClosed } from "utils/pool/is-pool-closed";
import { showSignIn } from "state/slices/login-modals-slice";
import JoinGroupModal from "home/join-group-button/join-group-modal";
import ContestModal from "components/contests/contest-modal";
import { getContestById, getContestEntryById } from "state/slices/contests-slice";
import { postAttemptToPlaceABetMessage } from "utils/integration/iframe-messages";
import { isContestBettingBlocked, isPickPerPoolContest, remainingPicksForEntry } from "interfaces/leaderboard/contest";
import useContestPickedContestantIds from "hooks/useContestPickedContestantIds";
import useWallet from "hooks/useWallet";

interface HomepagePoolProps {
  pool: Pool;
  contestEntryId?: string;
  isContestPool?: boolean;
  handleSetSelectedEntry: (entry: Entry, pool: Pool) => void;
  isSelected: boolean;
  selectedEntry?: Entry;
  handleCancel: () => void;
  setSuccessfulBet: (bet: Bet) => void;
  eventIconUrl?: string;
}

const HomepagePool = ({
                        pool,
                        contestEntryId,
                        handleSetSelectedEntry,
                        isSelected,
                        selectedEntry,
                        handleCancel,
                        setSuccessfulBet,
                        eventIconUrl,
                      }: HomepagePoolProps) => {
  const auth = useAuth();
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const { checkStatus } = useGeolocation();
  const [showForm, setShowForm] = useState(false);
  const {
    isContestGroup,
    status,
    name: groupName,
    id: groupId,
  } = useAppSelector(getGroupState);
  const userHasJoinedGroup = status === UserGroupMembershipStatus.APPROVED;
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);
  const [showJoinContestModal, setShowJoinContestModal] = useState(false);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const hasNotJoinedContest = isContestGroup && !contestEntryId;

  const params = new URLSearchParams();
  if (contestEntryId) params.append(CONTEST_ENTRY_ID_QUERY_PARAM, contestEntryId);

  const { id: contestId } = useParams<{ id: string }>();
  if (contestId && !contestEntryId) params.append(CONTEST_ID_QUERY_PARAM, contestId);
  const contest = useAppSelector((state) => getContestById(state, contestId));
  const { balance: contestEntryBalance } = useWallet({ contestEntryId });
  const contestEntry = useAppSelector((state) => getContestEntryById(state, contestEntryId));
  // Picks remaining is derived from the entry, not the wallet balance, which
  // inflates when pool winnings are credited back into the entry.
  const remainingPicks = remainingPicksForEntry(contestEntry);
  const pickedContestantIds = useContestPickedContestantIds(pool.id, pool.entries, contestEntryId);
  const isPoolAlreadyPicked = isPickPerPoolContest(contest) && pickedContestantIds.size > 0;
  const isBettingBlocked = isContestBettingBlocked(contest, remainingPicks) || isPoolAlreadyPicked;
  const dimForContestBlock = isBettingBlocked && pool.status !== POOL_STATUS.SETTLED;

  const poolPageLink = `/pools/${pool.id}${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  const isAllowedToBet = async () => {
    if (isPoolClosed(pool.status)) {
      navigate(poolPageLink);
      return false;
    }

    if (isBettingBlocked) {
      navigate(poolPageLink);
      return false;
    }

    if (integratedAppState.isIntegratedApp) {
      postAttemptToPlaceABetMessage(pool.id);
      if (integratedAppState?.userId) {
        return true;
      }
    }

    if (!auth.signedIn) {
      dispatch(showSignIn());
      return false;
    }

    const geoResult = await checkStatus();
    // not allowed geolocation
    if (userHasJoinedGroup) {
      if (![CheckRequestStatus.ALLOWED, SKIP].includes(geoResult.status)) {
        navigate(poolPageLink);
        return false;
      }
    }

    // this covers not signed in and not joined group
    if (groupName && !userHasJoinedGroup) {
      setShowJoinGroupModal(true);
      return false;
    }

    // not joined contest
    if (hasNotJoinedContest) {
      setShowJoinContestModal(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (isSelected) {
      setTimeout(() => setShowForm(true), 200);
    }

    if (!isSelected) {
      setTimeout(() => setShowForm(false), 200);
    }
  }, [isSelected]);

  const setSelected = async (entry: Entry) => {
    const allowed = await isAllowedToBet();

    if (!allowed) {
      return false;
    }
    handleSetSelectedEntry(entry, pool);
  };

  const onSuccessfulBetPlaced = (bet: any) => {
    dispatch(setWalletIsStale(true));
    dispatch(
      fetchBets({
        groupId,
        contestEntryId: contestEntryId || "",
        isIntegratedApp: integratedAppState.isIntegratedApp,
      })
    );

    setSuccessfulBet(bet);
  };

  return (
    <>
      <div
        className={cardFlipStyles.cardFlipInner}
        style={{
          transform: isSelected ? "rotateY(180deg)" : "rotateY(0deg)",
          opacity: dimForContestBlock ? 0.5 : 1,
        }}
      >
        {!showForm && (
          <FrontCard
            poolPageLink={poolPageLink}
            pool={pool}
            handleSetSelectedEntry={setSelected}
            isAllowedToBet={isAllowedToBet}
            contestEntryId={contestEntryId}
            contest={contest}
            contestEntryBalance={contestEntryBalance}
            eventIconUrl={eventIconUrl}
          />
        )}
        {showForm && (
          <HomepageBetForm
            contestEntryId={contestEntryId}
            contest={contest}
            pool={pool}
            entry={selectedEntry}
            cancel={handleCancel}
            onSuccessfulBetPlaced={onSuccessfulBetPlaced}
          />
        )}
      </div>
      <JoinGroupModal
        acceptAndJoin={() => {
          dispatch(requestToJoinGroup(groupId));
          setShowJoinGroupModal(false);
          if (hasNotJoinedContest) {
            setShowJoinContestModal(true);
          }
        }}
        show={showJoinGroupModal}
        close={() => setShowJoinGroupModal(false)}
      />
      {isContestGroup && contest && (
        <ContestModal
          contest={contest}
          show={showJoinContestModal}
          setShow={setShowJoinContestModal}
        />
      )}
    </>
  );
};

export default HomepagePool;
